import type { ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import PortalShell from './layouts/PortalShell';
import HomePage from './pages/HomePage';
import InvoicesPage from './pages/InvoicesPage';
import InvoiceDetailPage from './pages/InvoiceDetailPage';
import NewInvoicePage from './pages/NewInvoicePage';
import OnboardingPage from './pages/OnboardingPage';
import EmaraTaxCallbackPage from './pages/EmaraTaxCallbackPage';
import PeppolRegistrationPage from './pages/PeppolRegistrationPage';
import InboxPage from './pages/InboxPage';
import ApprovalsPage from './pages/ApprovalsPage';
import ArchivePage from './pages/ArchivePage';
import CompliancePage from './pages/CompliancePage';
import TemplatesPage from './pages/TemplatesPage';
import SettingsPage from './pages/SettingsPage';
import ProfilePage from './pages/ProfilePage';
import SpendPage from './pages/SpendPage';
import VatCenterPage from './pages/VatCenterPage';
import RecoveryAuditPage from './pages/RecoveryAuditPage';
import MfaSetupPage from './pages/MfaSetupPage';
import KycUploadPage from './pages/KycUploadPage';
import UboPage from './pages/UboPage';
import DocumentsPage from './pages/DocumentsPage';
import TaxFilingsPage from './pages/TaxFilingsPage';
import TaxFilingDetailPage from './pages/TaxFilingDetailPage';
import BankingPage from './pages/BankingPage';
import AccountingPage from './pages/AccountingPage';
import AgingPage from './pages/AgingPage';
import MyTasksPage from './pages/MyTasksPage';
import NotificationPrefsPage from './pages/NotificationPrefsPage';
import TaxDashboardPage from './pages/TaxDashboardPage';
import ChatPage from './pages/ChatPage';
import LoginPage from './pages/LoginPage';
import { useSession } from './auth/SessionContext';

function RequireAuth({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useSession();
  const location = useLocation();
  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }
  return <>{children}</>;
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route element={<RequireAuth><PortalShell /></RequireAuth>}>
        <Route index element={<HomePage />} />
        <Route path="/invoices" element={<InvoicesPage />} />
        <Route path="/invoices/:invoiceId" element={<InvoiceDetailPage />} />
        <Route path="/new" element={<NewInvoicePage />} />
        <Route path="/onboarding" element={<OnboardingPage />} />
        <Route path="/onboarding/emaratax/callback" element={<EmaraTaxCallbackPage />} />
        <Route path="/peppol-registration" element={<PeppolRegistrationPage />} />
        <Route path="/inbox" element={<InboxPage />} />
        <Route path="/approvals" element={<ApprovalsPage />} />
        <Route path="/templates" element={<TemplatesPage />} />
        <Route path="/spend" element={<SpendPage />} />
        <Route path="/vat" element={<VatCenterPage />} />
        <Route path="/recovery-audit" element={<RecoveryAuditPage />} />
        <Route path="/archive" element={<ArchivePage />} />
        <Route path="/compliance" element={<CompliancePage />} />
        <Route path="/settings/*" element={<SettingsPage />} />
        <Route path="/profile" element={<ProfilePage />} />
        {/* P0 routes added 2026-05-21 */}
        <Route path="/profile/mfa" element={<MfaSetupPage />} />
        <Route path="/onboarding/kyc" element={<KycUploadPage />} />
        <Route path="/onboarding/ubo" element={<UboPage />} />
        <Route path="/documents" element={<DocumentsPage />} />
        <Route path="/tax/filings" element={<TaxFilingsPage />} />
        <Route path="/tax/filings/:filingId" element={<TaxFilingDetailPage />} />
        {/* M4 + M8 + M11 P1 routes */}
        <Route path="/banking" element={<BankingPage />} />
        <Route path="/accounting" element={<AccountingPage />} />
        <Route path="/finance/aging" element={<AgingPage />} />
        <Route path="/my-tasks" element={<MyTasksPage />} />
        <Route path="/settings/notifications" element={<NotificationPrefsPage />} />
        <Route path="/tax/dashboard" element={<TaxDashboardPage />} />
        <Route path="/chat" element={<ChatPage />} />
      </Route>
    </Routes>
  );
}
